/**
 * [银行账户信息]
 * @author: Fu Xiaochun
 * @date: 2022-05-30 
 */

import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import styles from './index.module.scss';
import { Input, InputNumber, Select, message, Radio, } from 'antd';
import { getOpenBank, getBranchBank, getBankNameByCardId } from '@/apis/common';
import { GenerateUUID } from '@/util/utils';
import PropTypes from 'prop-types';

const { Option } = Select;

function BankInfo(props, ref) {

    // 银行信息
    const [bankAccountType, setBankAccountType] = useState('BANKACCT_PRI');  // 银行账户类型
    const [bankAccountName, setBankAccountName] = useState(props.bankAccountName); // 银行账户名称
    const [bankAccountId, setBankAccountId] = useState();  // 银行卡号
    const [bank, setBank] = useState();  // 开户行
    const [subBank, setSubBank] = useState();  // 开户支行
    const [bankTel, setBankTel] = useState(); // 银行预留电话
    const [bankList, setBankList] = useState([]); // 银行列表
    const [subBankList, setSubBankList] = useState([]); // 银行支行列表

    const $bank = useRef({ pageNum: 1, pageSize: 30 });
    const $subBank = useRef({ pageNum: 1, pageSize: 30 });
    const bankLoading = useRef(false);
    const subBankLoading = useRef(false);

    useEffect(() => {
        if (props.detail.id) {
            initData();
        }
    }, [props.detail]);

    const initData = () => {
        let detail = props.detail;
        console.log(detail);
        let _bankAccountName = detail.identityInfo ? detail.identityInfo.realName : detail.bankAccountName;
        setBankAccountType(detail.bankType || 'BANKACCT_PRI');
        setBankAccountName(_bankAccountName);
        setBankAccountId(detail.bankAccount);
        // setBank({ label: detail.openBank, value: detail.openBankCode });
        // setSubBank({ label: detail.bankName, value: detail.branchCode });
        setBankTel(detail.bankTel);
    };

    useEffect(() => {
        getOpenBankList();
    }, [bankAccountType]);

    useEffect(() => {
        setBankAccountName(props.bankAccountName);
    }, [props.bankAccountName]);

    useEffect(() => {
        $subBank.current.pageNum = 1;
        setSubBank();
        setSubBankList([]);
        if (bank && bank.value) {
            getBranchBankList();
        }
    }, [bank]);

    // 获取开户行
    const getOpenBankList = (data = {}) => {
        if (bankLoading.current) {
            return false;
        }
        let params = {
            bankAccountType,
            defBankAbbrCode: data.defBankAbbrCode,
            bankShortName: data.bankShortName,
            pageSize: $bank.current.pageSize,
            pageNum: $bank.current.pageNum
        };
        bankLoading.current = true;
        getOpenBank(params).then(res => {
            let _list = [...bankList];
            if ($bank.current.pageNum > 1) {
                _list = _list.concat(...res.result.dataList);
            } else {
                _list = res.result.dataList;
            }
            setBankList(_list);
        }).catch(err => {
            message.error(err.message);
        }).finally(() => {
            bankLoading.current = false;
        });
    };

    // 获取开户行支行
    const getBranchBankList = (data = {}) => {
        if (subBankLoading.current) {
            return false;
        }
        let params = {
            bankAbbrCode: bank.value,
            defBranchCode: data.defBranchCode,
            branchName: data.branchName,
            pageSize: $subBank.current.pageSize,
            pageNum: $subBank.current.pageNum
        };
        subBankLoading.current = true;
        getBranchBank(params).then(res => {
            let _list = [...subBankList];
            if ($subBank.current.pageNum > 1) {
                _list = _list.concat([...res.result.dataList]);
            } else {
                _list = res.result.dataList;
            }
            setSubBankList(_list);
        }).catch(err => {
            message.error(err.message);
        }).finally(() => {
            subBankLoading.current = false;
        });
    };

    const bankAccountTypeChange = (e) => {
        setBankAccountType(e.target.value);
    };

    const bankAccountIdBlur = (e) => {
        let val = e.target.value.replace(/\D/g, '').substr(0, 30);
        setBankAccountId(val);
        if (bankAccountType == 'BANKACCT_PRI' && val && val.length >= 16) {
            getCurCardBank(val);
            setSubBank();
        }
    };

    const bankTelBlur = (e) => {
        let val = e.target.value.replace(/\D/g, '').substr(0, 11);
        setBankTel(val);
    };

    const getCurCardBank = (bankCardNo) => {
        getBankNameByCardId({ bankCardNo }).then(res => {
            res.result.bankType && setBank({ value: res.result.bankType, label: res.result.bankName });
        }).catch(err => {
            message.error(err.message);
            setBank();
        });
    };

    const onBankScroll = (e) => {
        e.persist();
        const { target } = e;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            if (!bankLoading.current) {
                $bank.current.pageNum = $bank.current.pageNum + 1;
                getOpenBankList();
            }
        }
    };

    const onBankSearch = (val) => {
        let reg = /^[\u4e00-\u9fa5]{1,40}$/;
        $bank.current.pageNum = 1;
        if (reg.test(val)) {
            getOpenBankList({ bankShortName: val });
        }
        if (!val) {
            if (bank?.value) {
                getOpenBankList({ defBankAbbrCode: bank.value });
            } else {
                getOpenBankList();
            }
        }

    };

    const onBankFocus = (e) => {
        e.target.scrollTop = 0;
        $bank.current.pageNum = 1;
        getOpenBankList({ defBankAbbrCode: bank?.value });
    };

    const onSubBankScroll = (e) => {
        e.persist();
        const { target } = e;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            if (!subBankLoading.current) {
                $subBank.current.pageNum = $subBank.current.pageNum + 1;
                getBranchBankList();
            }
        }
    };

    const onSubBankSearch = (val) => {
        let reg = /^[\u4e00-\u9fa5]{1,40}$/;
        $subBank.current.pageNum = 1;
        if (reg.test(val)) {
            getBranchBankList({ branchName: val });
        }
        if (!val) {
            if (subBank?.value) {
                getBranchBankList({ defBranchCode: subBank.value });
            } else {
                getBranchBankList({});
            }
        }

    };

    const onSubBankFocus = (e) => {
        if (!bank?.value) {
            message.error('请先选择开户行！');
            return false;
        }
        e.target.scrollTop = 0;
        $subBank.current.pageNum = 1;
        getBranchBankList({ defBranchCode: subBank?.value });
    };

    useImperativeHandle(ref, () => ({
        getState: () => {
            return {
                bankAccountType,
                bankAccountName,
                bankAccountId,
                bankName: bank?.label,
                bankCode: bank?.value,
                subBankName: subBank?.label,
                subBankCode: subBank?.value,
                bankTel,
            };
        }
    }));

    const onSubBankChange = (val) => {
        console.log(val);
        setSubBank(val);
    };

    return (
        <div className={styles.bankInfo}>
            <div className={styles.item}>
                <div className={styles.label}>银行账户类型：</div>
                <div className={styles.val}>
                    <Radio.Group onChange={bankAccountTypeChange} value={bankAccountType}>
                        <Radio value="BANKACCT_PUB">对公</Radio>
                        <Radio value="BANKACCT_PRI">对私</Radio>
                    </Radio.Group>
                </div>
            </div>
            <div className={styles.item}>&nbsp;</div>
            <div className={styles.item}>
                <div className={styles.label}>银行账户名称：</div>
                <div className={styles.val}>
                    <Input className={styles.txt} value={bankAccountName} placeholder="请输入银行账户名称" maxLength={20} onChange={e => { setBankAccountName(e.target.value.trim()); }} />
                </div>
            </div>
            <div className={styles.item}>
                <div className={styles.label}>银行卡号：</div>
                <div className={styles.val}>
                    <Input className={styles.txt} allowClear value={bankAccountId} placeholder="请输入银行卡号" maxLength={30} onBlur={bankAccountIdBlur} onChange={e => { setBankAccountId(e.target.value.trim()); }} />
                </div>
            </div>
            <div className={styles.item}>
                <div className={styles.label}>开户行名称：</div>
                <div className={styles.val}>
                    <Select placeholder='请选择开户银行' className={styles.txt} allowClear showSearch
                        onFocus={onBankFocus}
                        onSearch={onBankSearch}
                        onChange={setBank}
                        value={bank}
                        onPopupScroll={onBankScroll}
                        optionFilterProp="children"
                        labelInValue={true}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                        {bankList.map((item, index) => <Option key={item.bankAbbrCode + '_' + index} value={item.bankAbbrCode} >{item.bankShortName}</Option>)}
                    </Select>
                </div>
            </div>
            <div className={styles.item}>
                <div className={styles.label}>开户支行名称：</div>
                <div className={styles.val}>
                    <Select placeholder='请选择开户银行支行' className={styles.txt} onChange={onSubBankChange} allowClear showSearch
                        onSearch={onSubBankSearch}
                        onFocus={onSubBankFocus}
                        value={subBank}
                        onPopupScroll={onSubBankScroll}
                        optionFilterProp="children"
                        labelInValue={true}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    >
                        {subBankList.map((item, index) => <Option key={item.branchCode + '_' + index} value={item.branchCode} >{item.branchName}</Option>)}
                    </Select>
                </div>
            </div>
            <div className={styles.item}>
                <div className={styles.label}>银行预留电话：</div>
                <div className={styles.val}>
                    <Input className={styles.txt} allowClear value={bankTel} placeholder="请输入银行预留电话" maxLength={11} onBlur={bankTelBlur} onChange={e => { setBankTel(e.target.value.trim()); }} />
                </div>
            </div>
        </div>
    );
}


const RefBankInfo = forwardRef(BankInfo);

BankInfo.propTypes = {
    detail: PropTypes.object,
};

BankInfo.defaultProps = {
    detail: {}
};


export default RefBankInfo;